import React, { useMemo } from 'react';
import type { WorkOrderDetails } from '#/graphql/types';
import RecordPreview from '#/common/components/RecordPreview';
import Styles from './Styles';
import { fsmWoFields, fsmWoliFields, FsmWorkOrderRecord } from './fields';
import { priorities, states, categories } from '../../mappings/mappings';
import { useNavigate } from 'react-router-dom';

export interface FsmWorkOrderPreviewProps {
  workOrder: WorkOrderDetails;
  lineItemsCount?: number;
}

const EMPTY_VALUE = '-';

const FsmWorkOrderPreview: React.FC<FsmWorkOrderPreviewProps> = ({ workOrder, lineItemsCount }) => {
  const navigate = useNavigate();

  const isLineItem = !workOrder.isParent && workOrder.parentId;

  const record: FsmWorkOrderRecord = useMemo(() => {
    const priorityEntry =
      Object.values(priorities).find((p) => p.value === workOrder.priority) ?? null;

    const stateEntry = workOrder.state
      ? Object.values(states).find((s) => s.key === workOrder.state)
      : null;

    const categoryEntry = workOrder.category
      ? Object.values(categories).find((c) => c.key === workOrder.category)
      : null;

    return {
      ...workOrder,
      locationName: workOrder.location?.name ?? null,
      assignedToName: workOrder.assignedTo?.name ?? null,
      serviceContractNumber: workOrder.serviceContract?.displayNumber ?? null,
      assetName: workOrder.asset?.assetTag ?? null,
      parentNumber: workOrder.thirdPartyNumber?.split('/')[0] ?? null,
      closedNotes: workOrder.closedNotes || null, // Handle empty string case to switch to the EMPTY_VALUE
      description: workOrder.description || null, // Handle empty string case to switch to the EMPTY_VALUE
      lineItemsCountValue: lineItemsCount ?? null,
      priorityLabel: priorityEntry?.label ?? null,
      state: stateEntry?.label ?? workOrder.state ?? null,
      category: categoryEntry?.label ?? workOrder.category ?? null,
    };
  }, [workOrder, lineItemsCount]);

  const fields = useMemo(() => {
    if (!workOrder) return [];

    if (isLineItem) {
      return fsmWoliFields(navigate);
    }

    return fsmWoFields(navigate);
  }, [navigate, isLineItem]);

  return (
    <Styles>
      <RecordPreview
        record={record}
        fields={fields}
        title={isLineItem ? 'Line Item Details' : 'FSM Work Order Details'}
        emptyValue={EMPTY_VALUE}
      />
    </Styles>
  );
};

export default React.memo(FsmWorkOrderPreview);
